import { Component, OnInit, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Apollo, gql } from 'apollo-angular';
import { ToastrService } from 'ngx-toastr';
import { cloneDeep } from 'lodash';

import { HeaderComponent } from '../header/header.component';
import { NavComponent } from '../navigation/nav.component';
import { AuthStore } from '@store/auth.store';
import { RideConfig } from '@interfaces/models.interface';
import { isUnsupportedByApi } from '../../../shared/utils/graphql-support.util';
import {
  isMissingPermissionError,
  MISSING_PERMISSION_MESSAGE,
} from '@shared/utils/admin-permission-error.util';
import { AdminPermission } from '@enums/admin-permission.enum';
import { AdminPermissionService } from '@services/admin-permission.service';

const RIDE_CONFIG_FIELDS = `
  bookingRideConfigId
  rideType
  title
  seatCount
  description
  icon
  tnvsBasePrice
  tnvsPricePerMin
  tnvsPriceKm
  status
  dateCreated
  dateUpdated
`;

// Deliberately not the public `bookingVehicle` query: that one drops rows with
// status = false, so a deactivated vehicle could never be found again here to
// turn back on.
const RIDE_CONFIGS = gql`
  query bookingRideConfigs {
    bookingRideConfigs { ${RIDE_CONFIG_FIELDS} }
  }
`;

const UPDATE_RIDE_CONFIG = gql`
  mutation updateBookingRideConfig($input: UpdateBookingRideConfigInput!) {
    updateBookingRideConfig(input: $input) { ${RIDE_CONFIG_FIELDS} }
  }
`;

const SERVICE_LABELS: Record<string, string> = {
  okride: 'OK! Ride',
  mototaxi: 'OK! MotoTaxi',
  oktaxi: 'OK! Taxi',
};

type RideConfigForm = {
  title: string;
  seatCount: number | null;
  description: string;
  icon: string;
  tnvsBasePrice: number | null;
  tnvsPricePerMin: number | null;
  tnvsPriceKm: number | null;
  status: boolean;
};

/**
 * TNVS fare configuration (CMS) — `tbl_booking_ride_config`.
 *
 * One row per bookable vehicle, holding the base fare and the per-minute and
 * per-kilometre rates the fare estimate is built from. Update only: rows are
 * seeded and the consumer apps render whatever is here, so adding or removing
 * one is a release-coordinated change rather than a CMS action.
 *
 * `rideType` is shown but never editable. It is the key the public
 * `bookingVehicle` query filters on, so changing it would move a vehicle
 * between services for every app already in users' hands.
 */
@Component({
  selector: 'app-ride-config-cms',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, HeaderComponent, NavComponent],
  templateUrl: './ride-config-cms.component.html',
  styleUrl: './ride-config-cms.component.css',
})
export class RideConfigCmsComponent implements OnInit {
  constructor(
    private apollo: Apollo,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private router: Router,
    private authStore: AuthStore,
    private permissions: AdminPermissionService,
  ) {}

  /** Editing is offered only with `tnvs_fares.manage` — the API refuses it otherwise. */
  get canManage(): boolean {
    return this.permissions.can(AdminPermission.TnvsFaresManage);
  }

  public configs: RideConfig[] = [];
  public isLoading = false;
  public loadError: 'none' | 'unsupported' | 'failed' = 'none';

  public showDetails = false;
  public enableEdit = false;
  public isSaving = false;
  public detailViewed: RideConfig | null = null;

  public form: RideConfigForm = this.emptyForm();

  private emptyForm(): RideConfigForm {
    return {
      title: '',
      seatCount: null,
      description: '',
      icon: '',
      tnvsBasePrice: null,
      tnvsPricePerMin: null,
      tnvsPriceKm: null,
      status: true,
    };
  }

  ngOnInit(): void {
    this.fetchConfigs();
  }

  serviceLabel(rideType: string): string {
    return SERVICE_LABELS[rideType] ?? rideType;
  }

  fetchConfigs(): void {
    this.isLoading = true;
    this.loadError = 'none';

    this.apollo
      .watchQuery({ fetchPolicy: 'network-only', query: RIDE_CONFIGS })
      .valueChanges.subscribe({
        next: (res: any) => {
          this.isLoading = false;
          const rows: RideConfig[] = res?.data?.['bookingRideConfigs'] ?? [];
          this.configs = cloneDeep(rows);

          if (this.detailViewed) {
            const refreshed = this.configs.find(
              (config) =>
                config.bookingRideConfigId === this.detailViewed!.bookingRideConfigId,
            );
            if (refreshed) this.detailViewed = refreshed;
          }
        },
        error: (error) => {
          this.isLoading = false;
          if (this.handledAuthError(error)) return;
          this.loadError = isUnsupportedByApi(error) ? 'unsupported' : 'failed';
        },
      });
  }

  viewDetails(config: RideConfig): void {
    this.detailViewed = config;
    this.showDetails = true;
    this.enableEdit = false;
    this.resetFormFromRecord();
  }

  returnToList(): void {
    this.showDetails = false;
    this.enableEdit = false;
    this.detailViewed = null;
    this.form = this.emptyForm();
  }

  enableEditForm(): void {
    this.enableEdit = true;
    this.resetFormFromRecord();
  }

  disableEditForm(): void {
    this.enableEdit = false;
    this.resetFormFromRecord();
  }

  private resetFormFromRecord(): void {
    const record = this.detailViewed;
    if (!record) {
      this.form = this.emptyForm();
      return;
    }

    this.form = {
      title: record.title ?? '',
      seatCount: record.seatCount ?? null,
      description: record.description ?? '',
      icon: record.icon ?? '',
      tnvsBasePrice: record.tnvsBasePrice ?? null,
      tnvsPricePerMin: record.tnvsPricePerMin ?? null,
      tnvsPriceKm: record.tnvsPriceKm ?? null,
      status: record.status,
    };
  }

  private blank(value: string | null | undefined): boolean {
    return (value ?? '').trim() === '';
  }

  private missing(value: number | null | undefined): boolean {
    return value === null || value === undefined || `${value}` === '';
  }

  private validationError(): string | null {
    const f = this.form;

    if (this.blank(f.title)) return 'Enter a title.';
    if (f.title.trim().length > 60) return 'Title must not exceed 60 characters.';
    if (this.blank(f.description)) return 'Enter a description.';
    if (f.description.trim().length > 200)
      return 'Description must not exceed 200 characters.';
    if (this.blank(f.icon)) return 'Enter an icon.';

    if (this.missing(f.seatCount) || !Number.isInteger(Number(f.seatCount)) || Number(f.seatCount) < 1) {
      return 'Seat count must be a whole number of at least 1.';
    }

    const prices: [number | null, string][] = [
      [f.tnvsBasePrice, 'Base fare'],
      [f.tnvsPricePerMin, 'Price per minute'],
      [f.tnvsPriceKm, 'Price per kilometre'],
    ];
    for (const [value, label] of prices) {
      if (this.missing(value)) return `Enter a ${label.toLowerCase()}.`;
      if (Number.isNaN(Number(value)) || Number(value) < 0) {
        return `${label} must be 0 or greater.`;
      }
    }

    return null;
  }

  confirmUpdate(modal: TemplateRef<any>): void {
    const error = this.validationError();
    if (error) {
      this.toastr.error(error);
      return;
    }

    this.modalService.open(modal, { backdrop: 'static', keyboard: false, size: 'lg' });
  }

  updateConfig(successModal: TemplateRef<any>): void {
    const record = this.detailViewed;
    const error = this.validationError();
    if (!record || error) {
      if (error) this.toastr.error(error);
      return;
    }

    this.isSaving = true;

    // rideType is intentionally not sent — the mutation does not accept it.
    const input = {
      bookingRideConfigId: record.bookingRideConfigId,
      title: this.form.title.trim(),
      seatCount: Number(this.form.seatCount),
      description: this.form.description.trim(),
      icon: this.form.icon.trim(),
      tnvsBasePrice: Number(this.form.tnvsBasePrice),
      tnvsPricePerMin: Number(this.form.tnvsPricePerMin),
      tnvsPriceKm: Number(this.form.tnvsPriceKm),
      status: this.form.status,
    };

    this.apollo.mutate({ mutation: UPDATE_RIDE_CONFIG, variables: { input } }).subscribe({
      next: ({ data }: any) => {
        this.isSaving = false;
        const updated: RideConfig | undefined = data?.['updateBookingRideConfig'];
        if (!updated) {
          this.toastr.error('The fare configuration was not updated. Please try again.');
          return;
        }

        this.detailViewed = cloneDeep(updated);
        this.configs = this.configs.map((config) =>
          config.bookingRideConfigId === updated.bookingRideConfigId
            ? cloneDeep(updated)
            : config,
        );
        this.resetFormFromRecord();

        this.modalService.dismissAll();
        this.enableEdit = false;
        this.modalService.open(successModal, {
          backdrop: 'static',
          keyboard: false,
          size: 'lg',
        });
      },
      error: (error) => {
        this.isSaving = false;
        this.modalService.dismissAll();
        if (this.handledAuthError(error)) return;
        this.toastr.error(error?.message || 'Error updating the fare configuration');
      },
    });
  }

  closeAllModals(): void {
    this.modalService.dismissAll();
  }

  private handledAuthError(error: any): boolean {
    // Refused by the admin's roles, not a dead session: say so and stay signed in.
    if (isMissingPermissionError(error)) {
      this.toastr.error(MISSING_PERMISSION_MESSAGE);
      return true;
    }

    const isAuthError =
      error?.message === 'Unauthorized' ||
      error?.networkError?.status === 403 ||
      error?.graphQLErrors?.[0]?.extensions?.status === 403 ||
      error?.errors?.[0]?.extensions?.status === 403;

    if (!isAuthError) return false;

    this.authStore.logout().subscribe(() => {
      this.router.navigate(['/admin/login']);
    });
    return true;
  }
}
